import { createContext, use } from "react";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import type {
  FieldIdToLookupReferences,
  FieldIdToValueMap,
  FieldsCitationsMap,
  FieldsConfidenceMap,
  LookupObjectReference,
  ObjectTypeToLookupReferences,
} from "../../api/types/objectTypes";
import type { FieldGroupModel } from "./entityFieldTypes";

export type EmailFieldEditMode =
  { fieldId: string; kind: "dialog"; onEdit: () => void } | { fieldId: string; kind: "disabled"; reason: string };

export interface FieldValuesState {
  fieldValues: FieldIdToValueMap;
  isValid: boolean;
  fieldEditState: "viewing" | "editing" | "canceling";
}

export interface Props {
  objectType: string;
  fieldValuesState: FieldValuesState;
  updateFieldValuesState: (state: Partial<FieldValuesState>) => void;
  fieldGroups: FieldGroupModel[];
  lookupObjects: FieldIdToLookupReferences;
  lookupOptionsByObjectType: ObjectTypeToLookupReferences;
  addLookupOption: (objectType: string, lookupObjectReference: LookupObjectReference) => void;
  onFieldValueChange: (value: unknown, fieldId: string, validationResult: ValidationResult) => void;
  validateUniqueFieldValue: (fieldId: string, value: unknown) => Promise<ValidationResult>;
  emailFieldEditMode?: EmailFieldEditMode;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  showFieldValueBorders?: boolean;
  fieldsAutoFillTooltips?: Record<string, string>;
}

export const FieldValuesContext = createContext<Props | undefined>(undefined);

export const useFieldValuesContext = () => {
  const context = use(FieldValuesContext);
  return defined(context, "Attempt to use FieldValuesContext outside of provider");
};
